<script setup lang="ts">
// Declare the evoPdfConverter_startConversion property on Window interface
declare global {
  interface Window {
    evoPdfConverter_startConversion?: () => void;
  }
}

const runningInEvoPdf =
  typeof window.evoPdfConverter_startConversion === "function";

const inIFrame = window.self !== window.top;

const enableDeveloperDebug =
  !inIFrame || localStorage.getItem("developerDebug") === "true"; // Set to true to enable developer debug logs

if (enableDeveloperDebug) {
  console.log("Developer debug mode enabled - WorkflowPdfViewer.vue");
  // for testing, directly open the URL in a new tab
  console.log(
    "URL",
    window.location.href,
    "Running in EvoPDF:",
    runningInEvoPdf,
    "In iframe:",
    inIFrame,
  );
}

import { ElMessage } from "element-plus";
import pLimit from "p-limit";
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import {
  FormElementBase,
  FormResponse,
  FormType,
  PdfMode,
  Workflow,
  WorkflowDto,
} from "~/api";
import {
  useWorkflow,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import FormResponseTemplate from "~/Compliance/pages/FormResponseTemplate.vue";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { getEnvConfig } from "~/services/appConfig";

// Cache the environment config for performance
const envConfig = getEnvConfig();

// Concurrency limit for image processing - prevents overwhelming the server
// while still allowing parallel processing for better performance
const IMAGE_PROCESSING_CONCURRENCY = 3;

import { useRoute } from "vue-router";
const route = useRoute();

import { authStore } from "~/stores/authStore";
const authToken = ref<string>();

import { useStorage } from "@vueuse/core";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { usePdfModeStore } from "~/stores/pdfModeStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { useFormResponse } from "../composables/useFormResponse";
const pdfLoadingStore = usePdfLoadingStore();

// Enter PDF mode early (during component setup, before child components render).
// This sets the reactive flag for all inPdf checks and also adds the "forPdf" class
// to <body> for CSS. We clean it up in onUnmounted.
usePdfModeStore().enterPdfMode();

type FormElementBaseForPdf = FormElementBase & {
  REMOVE_FOR_PDF?: boolean;
};

const sharedAnswers = ref<Record<string, string>>({});
const sharedAnswersForProps = computed(() => sharedAnswers.value);

const hasBeenSent = ref(false);

const workflow = ref<Workflow>();
const statusOptions = ref<{ code: string; label: string }[]>([]);

const pdfCode = Array.isArray(route.params.pdfCode)
  ? (route.params.pdfCode[0] ?? "")
  : (route.params.pdfCode ?? "");

const settings = computed(() => {
  // 2025-09 hard coded for nom. Need to make more generic.
  const simpleForm = pdfCode === "nom";
  return simpleForm
    ? {
        hideTitle: true,
        showHeaderFooter: false,
        hideFormsWithNoAnswers: true,
      }
    : {};
});

const loadWatcher = watch(
  () => pdfLoadingStore.allLoaded,
  (allLoaded) => {
    if (allLoaded && !hasBeenSent.value) {
      // console.log("All resources loaded");
      if (pdfLoadingStore.errors.length > 0) {
        showError("Some resources failed to load", pdfLoadingStore.errors);
      }
      // all resources are loaded (even if some failed), we can now send the HTML to the server
      if (runningInEvoPdf) {
        // Trigger conversion when counter reached a value
        if (window.evoPdfConverter_startConversion) {
          window.evoPdfConverter_startConversion();
        }
      } else {
        sendToServer();
      }
    }
  },
);
loadWatcher.pause();

const sendToServer = () => {
  // --> Sending HTML to server

  if (inIFrame) {
    // only do this if we are in an iframe
    addToLog(
      "In iFrame so will prepare and send HTML to server for PDF conversion",
    );

    // use a timeout to ensure the DOM is fully loaded
    setTimeout(async () => {
      await prepareForPdf();

      // use another timeout to ensure the DOM is fully prepared
      setTimeout(async () => {
        // take this entire page and send it to the server to convert to PDF
        const html = getHtml();

        const generatedPdfGuid = await apiClient.pdf.convertToPdf({
          html: html,
          htmlIsUrl: false,
          baseUrl: envConfig.serverUrl,
          id: workflow.value!.guid,
          pdfMode: PdfMode.WORKFLOW,
          includeHeadings: settings.value.showHeaderFooter ?? true,
          attachmentOrder: attachmentOrder.value,
          connectionId: localStorage.getItem("hubConnectionId") || "",
        });

        const pdfUrl = `${envConfig.serverUrl}/api/pdf/${generatedPdfGuid}/GetPdf`;

        location.href = pdfUrl;

        setTimeout(() => {
          // Navigate to a blank page to prevent re-sending
          location.href = "about:blank";
        }, 1000);
      }, 0);
    }, 0);
  } else {
    addToLog("Ready to send HTML");
    if (autoPrepareForPdf.value || runningInEvoPdf) {
      setTimeout(async () => {
        await prepareForPdf();
      }, 0);
    }

    console.log("Not in an iframe, not sending HTML to server");
  }
  hasBeenSent.value = true;
};

// add pdfDebugLog element if going to make a PDF
const pdfDebugLog = document.createElement("div");

if (enableDeveloperDebug) {
  pdfDebugLog.id = "pdfDebugLog";
  document.body.insertBefore(pdfDebugLog, document.getElementById("app"));
  // console.log("PDF Debug Log element created");
}

const getCompleteWorkflow = async (workflowInstanceDto: WorkflowDto) => {
  if (!workflowInstanceDto?.guid) {
    showError("Workflow instance not found");
    return;
  }
  try {
    const dto = await apiClient.workflowInstance.getWorkflow(
      workflowInstanceDto.guid,
      pdfCode || "main",
    );

    workflow.value = adjustWorkflow(dto.workflow);
    sharedAnswers.value = dto.sharedAnswers;

    document.title = "PDF: " + (workflow.value?.name ?? "");

    setTimeout(() => {
      pdfLoadingStore.componentLoaded();
    }, 1);
  } catch (error) {
    showError(error);
    showError("Failed to load workflow details");
    return;
  }
};

const workflowState = useWorkflow({
  updateStatus: async (status: string) => {
    if (!workflow.value?.guid) {
      showError("Workflow instance not found");
      return;
    }
    if (workflow.value.status === status) {
      // console.log("No need to update status");
      return;
    }
    workflow.value.status = status;
  },
  updateStatusFromNameStamp: async (status: string) => {
    if (!workflow.value?.guid) {
      showError("Workflow instance not found");
      return;
    }
    if (workflow.value.status === status) {
      // console.log("No need to update status");
      return;
    }
    workflow.value.status = status;
  },
  getWorkflowStatus: () => workflow.value?.status ?? "",
  getWorkflow: () => workflow.value,
});

// provide form state to child components
provide(workflowInjectKey, workflowState);

const preparedForPdf = ref<boolean>(false);

// Helper function to convert blob/http URLs to base64
const convertImageToBase64 = async (src: string): Promise<string> => {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${src.startsWith("blob:") ? "blob" : "image"}`,
    );
  }
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(
        new Error(
          `Error reading ${src.startsWith("blob:") ? "blob" : "image"}`,
        ),
      );
    reader.readAsDataURL(blob);
  });
};

// Setup base URL for PDF generation
const setupBaseUrl = () => {
  const baseUrl = envConfig.entraRedirect;
  pdfDebugLog.innerText += "Base URL for PDF: " + baseUrl + "\n";

  const existingBase = document.head.querySelector("base");
  if (!existingBase) {
    const base = document.createElement("base");
    base.href = baseUrl + "/";
    document.head.appendChild(base);
    pdfDebugLog.innerText += "Base element created in head\n";
  } else {
    existingBase.setAttribute("href", baseUrl + "/");
    pdfDebugLog.innerText += "Updated element in head\n";
  }
};

// Process uploaded images for PDF
const processUploadedImages = async (log: string[]) => {
  const imagesAtEndOfPdf = document.getElementById("imagesAtEndOfPdf");
  if (!imagesAtEndOfPdf) {
    log.push("No imagesAtEndOfPdf element found, skipping image processing");
    return;
  }

  const imageHolders = document.querySelectorAll(".el-upload-list__item");
  log.push("Found " + imageHolders.length + " images for PDF");

  // Limit concurrent image processing to prevent server overload
  const limit = pLimit(IMAGE_PROCESSING_CONCURRENCY);

  await Promise.all(
    Array.from(imageHolders).map((holder) =>
      limit(() => processImageHolder(holder, log)),
    ),
  );
};

const attachmentOrder = ref([] as string[]);

// Process individual image holder
const processImageHolder = async (holder: Element, log: string[]) => {
  const name = holder.getElementsByClassName(
    "el-upload-list__item-file-name",
  )[0]?.textContent;
  const img = holder.getElementsByTagName("img")[0] as HTMLImageElement;

  log.push(
    `Uploaded Image - Name: ${name} - Src: ${img.src.substring(0, 30)}...`,
  );

  const fileGuid = img.src.split("#")[1];
  if (fileGuid) {
    attachmentOrder.value.push(fileGuid);
  } else if (img.src.startsWith("blob:") || img.src.startsWith("http")) {
    img.src = await convertImageToBase64(img.src);
    log.push(
      `Converted ${img.src.startsWith("blob:") ? "blob" : "image"} URL to base64 for PDF: ${img.src.substring(0, 50)}...`,
    );
  }
};

// Process rich text images
const processRichTextImages = async (log: string[]) => {
  const richTextImages = document.querySelectorAll(".mce-content-body img");
  // console.log("Found " + richTextImages.length + " rich text images for PDF");

  // Limit concurrent image processing to prevent server overload
  const limit = pLimit(IMAGE_PROCESSING_CONCURRENCY);

  await Promise.all(
    Array.from(richTextImages).map((element) =>
      limit(async () => {
        const img = element as HTMLImageElement;
        log.push("Rich text image src: " + img.src.substring(0, 50) + "...");

        if (img.src.startsWith("blob:")) {
          img.src = await convertImageToBase64(img.src);
          log.push(
            "Converted rich text blob URL to base64 for PDF: " +
              img.src.substring(0, 50) +
              "...",
          );
        }
      }),
    ),
  );
};

// Process input elements for PDF
const processInputElements = (log: string[]) => {
  const inputs = Array.from(
    document.documentElement.querySelectorAll("input, textarea, select"),
  ).filter((el) => {
    const isValidInput =
      (el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement) &&
      !(el.type === "checkbox" || el.type === "radio");

    if (!isValidInput) {
      log.push("Hiding input element: " + el.outerHTML);
    }

    return isValidInput;
  });

  log.push("Processing input elements for PDF: " + inputs.length);

  inputs.forEach((input) => {
    const typedInput = input as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement;
    if (typedInput.type === "file") {
      log.push("Removing file input");
      typedInput.parentNode?.removeChild(typedInput);
      return;
    }

    replaceInputWithSpan(typedInput, log);
  });
};

// Replace input element with span
const replaceInputWithSpan = (
  input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  log: string[],
) => {
  log.push(
    `Preparing input for PDF:${input.name} (${input.type}) ${input.value}`,
  );

  const span = document.createElement("span");
  span.textContent = input.value || "";
  span.className = "pdf-span pdf-span-" + input.type;
  input.parentNode?.replaceChild(span, input);
};

// Clean up UI elements for PDF
const cleanupUIElements = (log: string[]) => {
  // console.log("Cleaning up UI elements for PDF");
  // Remove upload item labels and icons
  const uploadItems = document.querySelectorAll(".el-upload-list__item");
  uploadItems.forEach((item) => {
    item.querySelector("label")?.remove();
    item.querySelector("i")?.remove();
  });
  log.push("Removed labels and icons from upload items: " + uploadItems.length);

  // adjust el-selects
  const selectElements = document.querySelectorAll(".el-select");
  selectElements.forEach((select) => {
    const span = document.createElement("span");
    span.textContent =
      (
        select.getElementsByClassName(
          "el-select__placeholder",
        )[0] as HTMLElement
      )?.innerText || "NOT FOUND";
    span.className = "pdf-span pdf-span-select";
    select.parentNode?.replaceChild(span, select);
  });
  log.push("Adjusted el-select elements: " + selectElements.length);

  // remove left-aligned from NameStampComponent
  const nameStampComponents = document.querySelectorAll(
    ".NameStampComponent.left-aligned",
  );
  nameStampComponents.forEach((component) =>
    component.classList.remove("left-aligned"),
  );
  log.push(
    "Adjusted NameStampComponent alignment: " + nameStampComponents.length,
  );

  // Remove copy to clipboard icons
  const copyIcons = document.querySelectorAll(".copyToClipboard");
  copyIcons.forEach((icon) => icon.remove());
  log.push("Removed copy to clipboard icons: " + copyIcons.length);

  // Remove cascader input suffix elements
  const cascaderInputs = document.querySelectorAll(
    ".el-cascader .el-input__suffix",
  );
  cascaderInputs.forEach((input) => input.remove());
  log.push("Removed cascader input suffix elements: " + cascaderInputs.length);

  // Remove tooltip trigger elements
  const tooltips = document.querySelectorAll("i.el-icon.el-tooltip__trigger");
  tooltips.forEach((tooltip) => tooltip.remove());
  log.push("Removed tooltip trigger elements: " + tooltips.length);

  // Remove tooltip trigger elements
  const stampIcons = document.querySelectorAll(".stamp i.el-icon");
  stampIcons.forEach((icon) => icon.remove());
  log.push("Removed stamp icons: " + stampIcons.length);

  // Remove all scripts
  const scripts = document.querySelectorAll("script");
  scripts.forEach((script) => script.remove());
  log.push("Removed scripts from head and body: " + scripts.length);
};

/**
 * Inlines all currently loaded stylesheets (both <link> and <style> ones) into a single
 * <style data-pdf-inlined-styles> element near the top of <head>.
 *
 * Internal / same-origin stylesheets (Vite-extracted element-plus, index, userlist, plus all
 * the Vue SFC <style> blocks) are inlined as literal CSS text and their <link> tags are removed.
 * This is the key fix for production PDF generation: the backend converter receives a mostly
 * self-contained HTML instead of <link href="./assets/xxx.css"> that it can't resolve.
 *
 * External/public CDN stylesheets (different origin, e.g. the MDI icons CDN) are left in the
 * document as <link> tags. We can't read their cssRules client-side (CORS), but the EvoPDF
 * Chromium on the server can fetch public CDNs directly when it renders the PDF.
 */
const inlineStylesheetsForPdf = (log: string[]) => {
  const inlinedBlocks: string[] = [];
  let inlinedCount = 0;
  let skippedCount = 0;

  // Snapshot the list first
  const sheets = Array.from(document.styleSheets);

  sheets.forEach((sheet, index) => {
    const href =
      (sheet as CSSStyleSheet).href ||
      (
        sheet.ownerNode as HTMLLinkElement | HTMLStyleElement | null
      )?.getAttribute("href") ||
      "inline-or-constructed";

    try {
      const rules = (sheet as CSSStyleSheet).cssRules;
      if (rules && rules.length > 0) {
        let cssText = "";
        for (let r = 0; r < rules.length; r++) {
          cssText += rules[r].cssText + "\n";
        }
        if (cssText.trim().length > 0) {
          inlinedBlocks.push(
            `/* === inlined stylesheet #${index} (${href}) === */\n${cssText}`,
          );
          inlinedCount++;
        }
      }
    } catch (err: any) {
      skippedCount++;
      log.push(
        `Skipped stylesheet #${index} (${href}): ${err?.message || err} (cross-origin or inaccessible — e.g. MDI CDN)`,
      );
    }
  });

  if (inlinedBlocks.length > 0) {
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-pdf-inlined-styles", "true");
    styleEl.textContent = inlinedBlocks.join("\n\n");

    // Insert near the top of <head> so these rules have good cascade priority
    const firstChild = document.head.firstChild;
    if (firstChild) {
      document.head.insertBefore(styleEl, firstChild);
    } else {
      document.head.appendChild(styleEl);
    }
    log.push(
      `Inlined ${inlinedCount} stylesheet block(s) (total ${inlinedBlocks.length} sources) as <style data-pdf-inlined-styles>`,
    );
  } else {
    log.push("No stylesheets could be inlined");
  }

  if (skippedCount > 0) {
    log.push(
      `Note: ${skippedCount} stylesheet(s) could not be read (cross-origin).`,
    );
  }

  // Remove internal stylesheet links (the Vite-built ones we just inlined, plus any other
  // app-relative stylesheets). These would 404 or resolve wrongly when the backend converter
  // processes the HTML (because baseUrl is the API server, not the frontend).
  //
  // IMPORTANT: Leave external/public CDN stylesheets (different host, e.g. MDI icons) in place.
  // The EvoPDF converter can fetch public resources directly. We already couldn't read their
  // rules client-side due to CORS, so leaving the <link> lets the converter load them.
  let removedStylesheetCount = 0;
  document.querySelectorAll('link[rel="stylesheet"]').forEach((linkEl) => {
    const link = linkEl as HTMLLinkElement;
    const href = link.href || link.getAttribute("href") || "";
    // Treat as "external/public" if it has a protocol and is not on the current page's host.
    const isExternalPublic =
      /^https?:\/\//i.test(href) && !href.includes(location.host);
    if (isExternalPublic) {
      log.push(`Left external stylesheet link for converter to fetch: ${href}`);
    } else {
      link.remove();
      removedStylesheetCount++;
    }
  });

  // Aggressively remove other non-essential head links for the PDF snapshot
  const otherLinks = document.querySelectorAll(
    'link[rel="modulepreload"], link[rel="icon"], link[rel="preload"]',
  );
  otherLinks.forEach((el) => el.remove());

  const totalRemoved = removedStylesheetCount + otherLinks.length;
  if (totalRemoved > 0) {
    log.push(
      `Removed ${totalRemoved} <link> elements (internal stylesheets + modulepreloads, icons, preloads). ` +
        `${removedStylesheetCount} internal stylesheet(s) removed after inlining.`,
    );
  }

  // Also remove the original small body{} style from index.html if present (our inlined + body.forPdf rules cover it)
  const initialBodyStyle = document.head.querySelector(
    "style:not([data-pdf-inlined-styles])",
  );
  if (
    initialBodyStyle &&
    initialBodyStyle.textContent?.includes("background-color: #e0e0db")
  ) {
    initialBodyStyle.remove();
    log.push("Removed initial body background style from index.html");
  }
};

// This function retrieves the HTML of the entire page, including form inputs
const prepareForPdf = async () => {
  // console.log("Preparing for PDF conversion");
  pdfDebugLog.innerText += "Preparing for PDF conversion\n";

  setupBaseUrl();

  const log: string[] = [];

  // MUST be done before getHtml(). This turns the external <link> CSS files
  // (which don't survive the trip to the backend converter) into actual
  // inlined rules. Also cleans up modulepreload links etc.
  inlineStylesheetsForPdf(log);

  await Promise.all([
    processUploadedImages(log),
    processRichTextImages(log),
    Promise.resolve().then(() => {
      processInputElements(log);
      cleanupUIElements(log);
    }),
  ]);

  // Add table of contents spot
  // only do this if we have multiple forms...
  const numSteps = document.getElementsByClassName("workflow-step").length;
  if (numSteps > 1) {
    const divs = document.getElementsByClassName("summary-section");
    if (divs.length > 0) {
      pdfDebugLog.innerText += `Adding table of contents spot to the document (${numSteps} workflow steps)\n`;
      divs[0].insertAdjacentHTML(
        "afterend",
        "<div id='html-to-pdf-toc'></div>",
      );
    } else {
      pdfDebugLog.innerText += `No summary-section found for table of contents (${numSteps} workflow steps)\n`;
    }
  }

  log.push("Prepare complete");
  pdfDebugLog.innerText += log.join("\n") + "\n";
  preparedForPdf.value = true;
};

const getHtml = () => {
  // Get the HTML of the entire page
  const html = document.documentElement.outerHTML;
  return html;
};

onMounted(async () => {
  // Note: body.forPdf class (and reactive flag) is now set early via usePdfModeStore().enterPdfMode()
  // at the top of setup, so that child component inPdf checks see the correct value during initial render.

  // Set <base> early so that relative asset URLs (images, etc.) resolve correctly
  // during initial render, especially in sub-path deployments and when directly
  // opening the PDF page for debugging. prepareForPdf will ensure it again.
  setupBaseUrl();

  // need to have this here so that the loadingStore works even if there are no tracked components
  pdfLoadingStore.componentBeingLoaded();

  authToken.value = (await authStore.getAccessTokenAsync()) ?? undefined;

  // Get the workflow from the route params
  if (route.params.workflowGuid) {
    const workflowGuid = route.params.workflowGuid as string;
    try {
      const workflowDto = { guid: workflowGuid } as WorkflowDto;
      await getCompleteWorkflow(workflowDto);
    } catch (error) {
      showError("Failed to load workflow:", error);
    }
  } else {
    showError("No workflow specified");
  }

  statusOptions.value =
    workflow.value?.workflowDefinition?.statusList
      ?.map((s) => ({
        code: s.code,
        label: s.label,
      }))
      .filter(
        (status): status is { code: string; label: string } =>
          status.code !== null && status.label !== null,
      ) ?? [];

  // console.log("Workflow mounted:", workflow.value);
  setTimeout(() => {
    // console.log("Resuming load watcher");
    loadWatcher.resume();
  }, 1000);
});

onUnmounted(() => {
  usePdfModeStore().exitPdfMode();
});

const singleForm = computed(() => {
  return workflow.value?.formResponses?.[0] ?? null;
});

const addToLog = (msg: string) => {
  pdfDebugLog.innerText += msg + "\n";
};

const dumpHtml = () => {
  const html = getHtml();
  // if (pdfDebugLog) {
  //   pdfDebugLog.innerText = html;
  // }
  // console.log(html);

  // copy html to clipboard
  navigator.clipboard
    .writeText(html)
    .then(() => {
      ElMessage.success("HTML copied to clipboard");
    })
    .catch((err) => {
      showError("Failed to copy HTML to clipboard", err);
    });
};

const getWorkflowStatusLabel = (statusCode: string) => {
  return statusOptions.value?.find((status) => status.code === statusCode)
    ?.label;
};

/**
 *
 * @param wf The workflow to adjust
 * @returns The adjusted workflow
 */
function adjustWorkflow(wf: Workflow): Workflow {
  if (!wf) {
    return wf;
  }
  // only keep the workflow steps that are of type WORKFLOW_STEP
  // and sort them by sortOrder
  // console.log("Adjusting workflow:", wf);
  wf.formResponses =
    wf.formResponses?.filter(
      (f) =>
        (!f.formType || f.formType === FormType.WORKFLOW_STEP) &&
        (!settings.value.hideFormsWithNoAnswers ||
          (f.answers && f.answers.length > 0)),
    ) ?? [];

  // we need to look at each form element in the definition, and remove any that are not needed for the PDF
  // recursively adjust the form definitions
  // The Form definition is repeated twice... in the workflow definition and in each form response as that form's definition
  // We will only adjust the one in the workflow definitions.
  // Components is a flat list of all components in the form definition. They use parentGuid to link to sections and repeaters.
  wf.formResponses.forEach((fr) => {
    fr.formDefinition = wf.workflowDefinition?.formDefinitions?.find(
      (fd) => fd.guid === fr.formDefinitionGuid,
    );
    if (fr.formDefinition) {
      fr.formDefinition.components = filterComponentsList(
        fr.formDefinition.components,
      );
    }
  });

  // if a workflow step has no components, remove it
  wf.formResponses = wf.formResponses.filter(
    (fr) => fr.formDefinition && fr.formDefinition.components.length > 0,
  );

  wf.formResponses =
    wf.formResponses.sort(
      (a, b) =>
        (a.formDefinition?.sortOrder ?? 0) - (b.formDefinition?.sortOrder ?? 0),
    ) ?? [];

  return wf;
}

/**
 * Recursively filter the components list based on the PDF code.
 * @param components
 */
function filterComponentsList(
  components: FormElementBaseForPdf[],
): FormElementBaseForPdf[] {
  let newList = components.filter((c) => {
    if (!includeInPdf(c, components)) {
      // if this is excluded, then linked components should also be excluded
      // find all that claim this one as their parent, and mark them as REMOVE_FOR_PDF
      const associated = components.filter((cc) => cc.parentGuid === c.guid);
      associated.forEach((lc) => {
        lc.REMOVE_FOR_PDF = true;
      });

      return false;
    }
    return true;
  });

  // remove any remaining components that are marked as REMOVE_FOR_PDF
  newList = newList.filter((c) => !c.REMOVE_FOR_PDF);

  // remove any repeaters that have no linked questions
  newList = newList.filter((c) => {
    if (c.type === "Repeater") {
      const linkedQuestions = newList.filter((cc) => cc.parentGuid === c.guid);
      return linkedQuestions.length > 0;
    }
    return true;
  });

  // remove any sections that have no linked questions
  newList = newList.filter((c) => {
    if (c.type === "Section") {
      const linkedQuestions = newList.filter((cc) => cc.parentGuid === c.guid);
      return linkedQuestions.length > 0;
    }
    return true;
  });

  return newList;
}

function includeInPdf(
  c: FormElementBaseForPdf,
  components: FormElementBaseForPdf[],
): boolean {
  if (c.REMOVE_FOR_PDF) {
    return false;
  }
  if (c.isArchived) {
    return false;
  }

  if (pdfCode && (c.type === "Section" || c.type === "Repeater")) {
    // Sections and Repeaters are always included in the PDF unless they are empty
    return true;
  }

  const properties = c.properties || {};

  if (!pdfCode) {
    // if there is no pdfCode, then we want exclude any marked excludeFromMainPdf
    return !properties.excludeFromMainPdf;
  }

  const parentSectionRequiredByPdfCode = components.find(
    (parent) =>
      parent.guid === c.parentGuid &&
      parent.properties?.customPdfCodes?.includes(pdfCode),
  );

  if (parentSectionRequiredByPdfCode) {
    // If the parent section is required by the pdfCode, we want to include its children
    return true;
  }

  // if there is a pdfCode, then we want to show only those that match the pdfCode
  return properties.customPdfCodes?.includes(pdfCode) ?? false;
}

/**
 * Get the substituted title for a form step by applying template variable substitution
 * to the form definition name.
 */
const getSubstitutedFormTitle = (step: FormResponse): string => {
  if (!step.formDefinition?.name) {
    return step.formDefinition?.name ?? "Unknown Form";
  }

  // Only process titles that contain template variables
  if (!step.formDefinition.name.includes("{{")) {
    return step.formDefinition.name;
  }

  try {
    // Use the useFormResponse composable to access template substitution
    const formResponseState = useFormResponse({
      formDefinition: step.formDefinition,
      formResponse: step,
      displayMode: FormDisplayMode.READ_ONLY,
      workflow: workflow.value,
      sharedAnswers: sharedAnswers.value,
    });

    // Apply template substitution to the form name
    const substitutedTitle = formResponseState.substituteTemplatedText(
      step.formDefinition.name,
    );

    return substitutedTitle ?? step.formDefinition.name;
  } catch (error) {
    console.warn("Error substituting form title:", error);
    return step.formDefinition.name;
  }
};
/**
 * Computed property that returns substituted titles for all workflow step forms.
 * This prevents recalculating on every render and improves performance.
 */
const substitutedFormTitles = computed(() => {
  const titlesMap = new Map<string, string>();

  workflow.value?.formResponses?.forEach((step) => {
    if (step.guid) {
      titlesMap.set(step.guid, getSubstitutedFormTitle(step));
    }
  });

  return titlesMap;
});

/**
 * Get substituted title for a specific form step by GUID.
 */
const getFormTitleByGuid = (step: FormResponse): string => {
  if (!step.guid) {
    return step.formDefinition?.name ?? "Unknown Form";
  }
  return (
    substitutedFormTitles.value.get(step.guid) ??
    step.formDefinition?.name ??
    "Unknown Form"
  );
};

const autoPrepareForPdf = useStorage("autoPrepareForPdf", true);
</script>

<template>
  <div id="workflow-container" class="forPdf">
    <div v-if="!inIFrame && enableDeveloperDebug" class="devOptions">
      <el-checkbox v-model="autoPrepareForPdf">Auto Prepare</el-checkbox>
      <el-button :disabled="preparedForPdf" @click="prepareForPdf"
        >Prepare for PDF</el-button
      >
      <el-button @click="dumpHtml">Copy HTML</el-button>
      <span> Total: {{ pdfLoadingStore.totalComponents }} </span>
      <span> Loaded: {{ pdfLoadingStore.loadedComponents }}</span>
      <span> PDF Code: {{ pdfCode || "(main)" }}</span>
    </div>
    <div v-if="!settings.hideTitle" class="pdfTitle">
      <div class="workflow-info">
        <h1>
          {{ workflow?.name }}
        </h1>
      </div>
      <div class="workflowStatus">
        {{ getWorkflowStatusLabel(workflow?.status ?? "") }}
      </div>
    </div>

    <div
      v-if="workflow && (workflow.formResponses?.length ?? 0) > 1"
      class="workflow-in-pdf"
    >
      <div class="summary-section">
        <div id="html-to-pdf-toc"></div>
      </div>
      <div class="workflow-steps">
        <div
          v-for="(formResponse, index) in workflow.formResponses"
          :key="index"
          class="workflow-step"
        >
          <div class="step">
            <h2 class="step-header">
              <span class="form-title">{{
                getFormTitleByGuid(formResponse)
              }}</span>
            </h2>
            <div class="step-content">
              <FormResponseTemplate
                class="formInPdf"
                :form-guid="formResponse.formDefinition?.guid || ''"
                :response-guid="formResponse.guid"
                :response="formResponse"
                :workflow="workflow"
                :incoming-display-mode="FormDisplayMode.READ_ONLY"
                :pdf-viewer="true"
                :shared-answers="sharedAnswersForProps"
              />
            </div>
          </div>
        </div>
      </div>
      <div id="imagesAtEndOfPdf">
        <!-- filled dynamically -->
      </div>
    </div>

    <div v-else-if="singleForm" class="workflow-in-pdf">
      <div class="singleForm">
        <FormResponseTemplate
          class="formInPdf"
          :form-guid="singleForm.formDefinition?.guid || ''"
          :response-guid="singleForm.guid"
          :response="singleForm"
          :workflow="workflow"
          :incoming-display-mode="FormDisplayMode.READ_ONLY"
          :pdf-viewer="true"
          :shared-answers="sharedAnswersForProps"
        />
      </div>
      <div id="imagesAtEndOfPdf">
        <!-- filled dynamically -->
      </div>
    </div>
    <div v-else>
      <el-empty description="Loading..." />
    </div>
  </div>
</template>

<style lang="less">
/*

Notes:
- This style is for the PDF view of the workflow.
- EvoPDF does not correctly display flex gaps.


*/

.notInPdf {
  display: none;
}

body.forPdf {
  background-color: #ffffff;
  height: fit-content;
  overflow: visible;
  font-size: 10pt;
  margin: 0 auto;
  width: 612pt; /* 8.5 inches in points */

  @page {
    size: letter;
    margin: 0.75in 0.5in 0.5in; // key to formatting. Leave some margin for the PDF viewer's header and footer, and to prevent content from being cut off
  }

  .el-container,
  .compliance.mainContainer .homeMain,
  #app {
    height: fit-content;
    width: 100%;
    overflow: visible;
    padding: 0;
  }
  #app {
    position: relative;
  }
  .compliance.mainContainer .warningContainer {
    display: none;
  }
  .formInPdf {
    .formHost .form {
      border: none;
      padding: 0;
    }
  }

  #workflow-container {
    margin: 0;
  }

  h1 {
    line-height: 5rem;
    margin: 0;
    font-size: 1.35rem;
  }

  .workflow-in-pdf {
    button {
      display: none;
    }
    margin: 0;
  }

  .component-renderer.component-indent-level-0 .DividerComponent {
    margin-left: -6px;
  }

  // prepare for printing to PDF
  .el-input.is-disabled {
    input.el-input__inner {
      color: black !important;
      -webkit-text-fill-color: black !important;
    }
    .el-input__wrapper {
      background-color: transparent !important;
      width: fit-content;
      padding-left: 1em;
    }
  }

  .NumberComponent {
    .component-label-comment {
      gap: 0;
      .component-comment {
        margin: 0;
      }
    }
  }

  .el-input-number {
    .el-input__wrapper {
      text-align: right;
    }
  }
  // .el-icon.el-tooltip__trigger {
  //   display: none;
  // }

  .el-input__wrapper {
    justify-content: flex-end;
    &:has(.pdf-span) {
      box-shadow: none;
      background-color: transparent;
      border-bottom: 1px solid #000;
      border-radius: unset;
    }
    .el-input__prefix {
      display: none;
    }
  }

  #pdfDebugLog {
    // only shows if it has content
    white-space: pre-wrap;
    background-color: orange;
    padding: 0 10px;
    max-height: 25vh;
    overflow: auto;
  }

  .complianceFormRendererPage .form {
    box-shadow: none;
  }

  .RichTextComponent {
    margin: 0.5em 0 1em;
    .fake-mce-content-body {
      margin-top: 0.25em;
      padding: 0.5em 1.5em 1em;
      border: 1px solid #ccc;
      border-radius: 3px;
      box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      font-size: var(--el-font-size-base);
      //background-color: var(--el-color-info-light-9);
    }
  }

  .pdfTitle {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .status-entity-info {
      display: flex;
      align-items: center;
      gap: 30px;
    }
  }
  .pdf-span {
    min-height: 1.2em;
    min-width: 17em;
    box-sizing: content-box;
  }

  .pdf-span-select {
    border: 1px solid #e4e7ed;
    padding: 0.1em 0.25em;
    white-space: normal;
  }

  .workflowStatus {
    font-size: 1rem;
    color: var(--el-color-primary);
  }

  .workflow-steps {
    margin-top: 20px;

    .workflow-step {
      margin-bottom: 20px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;

      & + .workflow-step {
        page-break-inside: avoid;
      }

      .step-header {
        background-color: #f5f7fa;
        padding: 12px 20px;
        border-bottom: 1px solid #e4e7ed;
        font-size: 10pt;
        font-weight: normal;
        margin: 0;
      }

      .step-content {
        padding: 20px;
      }
    }
  }
  .singleForm {
    border: none;
  }

  p:last-child {
    margin-bottom: 0;
  }
  .el-input__suffix {
    padding-right: 0;
  }
  .el-input__wrapper {
    align-items: normal;
  }
  .el-upload-list {
    page-break-inside: auto;
  }
}
.devOptions {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px;
  margin: 5px 0;
  background-color: #f5f5f5;
}
</style>
